import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Box, CircularProgress, InputAdornment, MenuItem, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { C, tnum } from './tokens'
import { PICKER_MENUS, PICKER_MENU_ITEMS } from './newDeliveryQueries'
import { isCustomizable, itemPrice, variantKeysOf } from './menuLine'
import ItemOptionsDialog from './ItemOptionsDialog'

const money = n => `$${(Number(n) || 0).toFixed(2)}`
const byOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)

/**
 * The restaurant's menu for a manual delivery: pick a menu, search, tap an
 * item. One with sizes or modifiers opens its options first; anything else goes
 * straight onto the order. Prices are the restaurant's base prices.
 */
export default function MenuItemPicker({ restaurantId, onAdd }) {
  const [menuId, setMenuId] = useState(null)
  const [search, setSearch] = useState('')
  const [optionsFor, setOptionsFor] = useState(null)

  const { data: menusData, loading: menusLoading } = useQuery(PICKER_MENUS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })
  const menus = useMemo(
    () => (menusData?.advancedMenus || []).filter(m => m.isActive).slice().sort(byOrder),
    [menusData],
  )
  const menu = menus.find(m => m._id === menuId) || menus[0] || null

  const { data: itemsData, loading: itemsLoading } = useQuery(PICKER_MENU_ITEMS, {
    variables: { menuId: menu?._id },
    skip: !menu?._id,
    fetchPolicy: 'cache-and-network',
  })
  const items = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (itemsData?.advancedMenuItems || [])
      .filter(i => i.isActive && i.isAvailable && !i.is86ed)
      .filter(i => !q || i.name.toLowerCase().includes(q) || (i.categoryName || '').toLowerCase().includes(q))
      .slice()
      .sort(byOrder)
  }, [itemsData, search])

  const byCategory = useMemo(() => {
    const groups = new Map()
    items.forEach(i => {
      const c = i.categoryName || 'Menu'
      if (!groups.has(c)) groups.set(c, [])
      groups.get(c).push(i)
    })
    return [...groups.entries()]
  }, [items])

  const pick = item => {
    if (isCustomizable(item)) {
      setOptionsFor(item)
      return
    }
    onAdd({
      itemId: item._id,
      menuId: menu?._id,
      name: item.name,
      unitPrice: itemPrice(item, null),
      modifierSelections: [],
      quantity: 1,
    })
  }

  // "from $8.00" when the price depends on the size picked.
  const priceLabel = item => {
    const sizes = variantKeysOf(item)
    return sizes.length ? `from ${money(itemPrice(item, sizes[0]))}` : money(itemPrice(item, null))
  }

  const loading = menusLoading || (itemsLoading && !itemsData)

  return (
    <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1, p: 1, borderBottom: `1px solid ${C.border}`, bgcolor: C.subtle }}>
        {menus.length > 1 && (
          <TextField
            select
            size="small"
            value={menu?._id || ''}
            onChange={e => setMenuId(e.target.value)}
            inputProps={{ 'aria-label': 'Menu' }}
            sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 34, fontSize: 13, bgcolor: C.card } }}
          >
            {menus.map(m => <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>)}
          </TextField>
        )}
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search the menu"
          inputProps={{ 'aria-label': 'Search the menu' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: C.muted }} /></InputAdornment>,
            sx: { height: 34, fontSize: 13, bgcolor: C.card },
          }}
        />
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
        )}
        {!loading && !menus.length && (
          <Box sx={{ p: 2, fontSize: 13, color: C.muted }}>This restaurant has no active menu.</Box>
        )}
        {!loading && menus.length > 0 && !items.length && (
          <Box sx={{ p: 2, fontSize: 13, color: C.muted }}>{search ? `Nothing matches “${search}”.` : 'No items on this menu.'}</Box>
        )}
        {byCategory.map(([category, list]) => (
          <Box key={category}>
            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: C.faint }}>
              {category}
            </Box>
            {list.map(item => (
              <Box
                key={item._id}
                component="button"
                type="button"
                onClick={() => pick(item)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 1.5, py: 1, border: 0, bgcolor: 'transparent',
                  textAlign: 'left', cursor: 'pointer', font: 'inherit', '&:hover': { bgcolor: C.mutedBg },
                }}
              >
                <Box sx={{ flex: 1, fontSize: 14, color: C.text }}>{item.name}</Box>
                <Box sx={{ fontSize: 13, color: C.muted, ...tnum }}>{priceLabel(item)}</Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <ItemOptionsDialog
        item={optionsFor}
        menuId={menu?._id}
        onClose={() => setOptionsFor(null)}
        onAdd={line => {
          onAdd(line)
          setOptionsFor(null)
        }}
      />
    </Box>
  )
}
